import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import {
  AXIS_COLOR,
  AXIS_TICK,
  GRID_COLOR,
  SURFACE,
  yAxisTitle,
} from "../../UI/Chart/chartStyle.ts";
import { ChartLegendItem } from "../../UI/Chart/ChartLegendItem.tsx";
import { WeeklyAvailability } from "./availabilityStats.ts";
import { shareOf } from "./shareOf.ts";
import { formatDateRange, formatHours, formatShare } from "./activityFormat.ts";
import {
  BOOKED_HOURS_COLOR,
  FREE_HOURS_COLOR,
} from "./teacherActivityColors.ts";

const HOVERED_WEEK_WASH = "#f0efec";

type WeeklyAvailabilityPoint = WeeklyAvailability & { label: string };

function TooltipRow({
  color,
  label,
  value,
}: {
  color?: string;
  label: string;
  value: string;
}) {
  return (
    <div className={"flex items-center justify-between gap-3"}>
      <span className={"flex items-center gap-1.5 text-slate-500"}>
        {color && (
          <span
            className={"w-2.5 h-2.5 rounded-sm"}
            style={{ backgroundColor: color }}
          />
        )}
        {label}
      </span>
      <span className={"font-semibold text-slate-800"}>{value}</span>
    </div>
  );
}

function WeeklyAvailabilityTooltip({
  active,
  payload,
}: TooltipProps<number, string>) {
  const week = payload?.[0]?.payload as WeeklyAvailabilityPoint | undefined;
  if (!active || !week) return null;

  return (
    <div
      className={
        "bg-white border border-slate-200 rounded-lg shadow-sm px-3 py-2 text-xs flex flex-col gap-1.5 min-w-48"
      }
    >
      <span className={"font-semibold text-slate-700"}>
        {formatDateRange(week.weekStart, week.weekStart.add(6, "day"))}
      </span>
      <TooltipRow
        color={BOOKED_HOURS_COLOR}
        label={"Booked"}
        value={formatHours(week.bookedHours)}
      />
      <TooltipRow
        color={FREE_HOURS_COLOR}
        label={"Left free"}
        value={formatHours(week.freeHours)}
      />
      <div className={"border-t border-slate-100 pt-1.5 flex flex-col gap-1"}>
        <TooltipRow
          label={"Availability offered"}
          value={formatHours(week.offeredHours)}
        />
        <TooltipRow
          label={"Booked share"}
          value={formatShare(shareOf(week.bookedHours, week.offeredHours))}
        />
      </div>
    </div>
  );
}

export function WeeklyAvailabilityChart({
  weeks,
}: {
  weeks: WeeklyAvailability[];
}) {
  const points = useMemo<WeeklyAvailabilityPoint[]>(
    () =>
      weeks.map((week) => ({ ...week, label: week.weekStart.format("D MMM") })),
    [weeks],
  );

  return (
    <div className={"flex flex-col gap-2"}>
      <div className={"flex flex-wrap items-center justify-between gap-2"}>
        <h3 className={"text-sm font-semibold text-slate-700"}>
          Availability per week
        </h3>
        <div className={"flex items-center gap-4"}>
          <ChartLegendItem
            color={BOOKED_HOURS_COLOR}
            label={"Booked"}
            shape={"block"}
          />
          <ChartLegendItem
            color={FREE_HOURS_COLOR}
            label={"Left free"}
            shape={"block"}
          />
        </div>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart
          data={points}
          margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
        >
          <CartesianGrid vertical={false} stroke={GRID_COLOR} />
          <XAxis
            dataKey="label"
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: AXIS_COLOR }}
            interval={"preserveStartEnd"}
            minTickGap={16}
          />
          <YAxis
            allowDecimals={false}
            label={yAxisTitle("Hours")}
            width={48}
            tick={AXIS_TICK}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            content={<WeeklyAvailabilityTooltip />}
            cursor={{ fill: HOVERED_WEEK_WASH }}
          />
          <Bar
            dataKey="bookedHours"
            name="Booked"
            stackId="availability"
            fill={BOOKED_HOURS_COLOR}
            stroke={SURFACE}
            strokeWidth={1}
            maxBarSize={32}
            isAnimationActive={false}
          />
          <Bar
            dataKey="freeHours"
            name="Left free"
            stackId="availability"
            fill={FREE_HOURS_COLOR}
            stroke={SURFACE}
            strokeWidth={1}
            radius={[4, 4, 0, 0]}
            maxBarSize={32}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
